import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePlus, HiOutlineChevronRight } from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import API from "../../api";
import { capture } from "../../instrument";
import { readListState, writeListState } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";

const DEFAULT_VIEWS = [{ _id: "ALL", name: "Tous", filters: [] }];

const STORAGE_KEY = "laboratory-list-state";

const Laboratory = () => {
  const history = useHistory();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");

  const {
    data: laboratories = [],
    isLoading: isLoadingLaboratories,
    isError: isErrorLaboratories,
  } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const response = await API.get("/admin/laboratory");
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const filterFields = useMemo(() => {
    return [
      {
        key: "hprimCode",
        label: "ID HPRIM",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["Renseigné", "Non renseigné"],
        resolve: (lab) => (lab.hprimCode ? "Renseigné" : "Non renseigné"),
      },
      {
        key: "isVisible",
        label: "Visibilité",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["Visible", "Masqué"],
        resolve: (lab) => (lab.isVisible !== false ? "Visible" : "Masqué"),
      },
    ];
  }, []);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "laboratory",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{laboratories.length}</span>;
    return null;
  };

  const filteredLaboratories = useMemo(() => {
    const result = applyFilters(laboratories);
    return rankBySearch(result, search, (lab) => [lab.name, lab.hprimCode, lab.address, lab.phone].filter(Boolean).join(" "));
  }, [laboratories, search, activeFilters]);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const virtualizer = useVirtualizer({
    count: filteredLaboratories.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const navigateToLaboratory = (lab) => {
    const ids = filteredLaboratories.map((c) => c._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(lab._id) });
    history.push({ pathname: `/laboratory/${lab._id}/edit`, state: { filteredIds: ids } });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredLaboratories.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredLaboratories.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const lab = filteredLaboratories[selectedIndexRef.current];
        if (!lab) return;
        if (newTab) window.open(`/laboratory/${lab._id}/edit`, "_blank");
        else navigateToLaboratory(lab);
      },
    },
    [filteredLaboratories, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/laboratory/add"), { preventDefault: true });

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  if (isLoadingLaboratories) {
    return <PageLoadingSpinner />;
  }

  if (isErrorLaboratories) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des laboratoires</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Laboratoires</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/laboratories", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => {
              history.push("/laboratory/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouveau laboratoire</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher un laboratoire..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredLaboratories.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const laboratory = filteredLaboratories[virtualRow.index];
                return (
                  <div
                    key={laboratory._id}
                    data-active={virtualRow.index === selectedIndex || undefined}
                    onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                    className="flex cursor-pointer items-center border-b border-gray-100 px-4 hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                    onClick={() => navigateToLaboratory(laboratory)}
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-gray-900">{laboratory.name}</span>
                    {laboratory.isVisible === false && (
                      <span className="ml-2 shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">Masqué</span>
                    )}

                    <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                      {laboratory.address && <span className="hidden max-w-[200px] truncate text-xs text-gray-500 lg:block">{laboratory.address}</span>}
                      {laboratory.phone && <span className="hidden text-xs text-gray-400 md:block">{laboratory.phone}</span>}
                      {laboratory.hprimCode ? (
                        <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">{laboratory.hprimCode}</code>
                      ) : (
                        <span className="text-xs text-gray-300">Pas de HPRIM</span>
                      )}
                      <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredLaboratories.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucun laboratoire trouvé"}</p>
              {!search && activeFilters.length === 0 && <p className="mt-1 text-xs">Créez-en un en cliquant sur le bouton ci-dessus</p>}
            </div>
          )}
        </div>
      </div>

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Laboratory;
